import classNames from 'classnames';
import { memo, type ReactNode } from 'react';

import {
  WorkspaceAgentTableCellContent,
  type WorkspaceAgentTableMonikerSegment,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/table-moniker/WorkspaceAgentTableCellContent';
import { type TablePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { type WorkspaceAgentTableRow } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/workspaceAgentTableArtifact';

export type WorkspaceAgentTablePillScrollportProps = {
  captionId: string | undefined;
  currentPage: number;
  pill: TablePill;
  renderMoniker: ((segment: WorkspaceAgentTableMonikerSegment) => ReactNode) | undefined;
  visibleRows: readonly WorkspaceAgentTableRow[];
};

/**
 * Column boxes are 10rem unless the header and cells are narrower (`max-width: max-content`).
 * A content-based width collapses to the longest word inside this 1px cell, which squashes a long
 * column. The definite 10rem width is what the table honors, and it is also what stops a nowrap
 * chip from setting the column width. Text wraps inside that cap. `inline-block` is what lets the
 * width apply; a block would stretch and a `td` max-width is ignored.
 *
 * The table fills the card. A trailing empty cell takes the leftover width so a stripe reaches
 * both edges, and collapses once the columns are wider than the card. `w-px` keeps each column
 * on its content box — without it the empty cell squeezes every column down to its longest word.
 */
export const WorkspaceAgentTablePillScrollport = memo(
  ({ captionId, currentPage, pill, renderMoniker, visibleRows }: WorkspaceAgentTablePillScrollportProps) => (
    <section
      aria-label={pill.title || 'Table'}
      className="min-w-0 overflow-x-auto overscroll-x-contain"
      data-testid="WORKSPACE_AGENT_TABLE_PILL_SCROLL"
      // biome-ignore lint/a11y/noNoninteractiveTabindex: scrollable region must be focusable for keyboard scrolling
      tabIndex={0} // NOSONAR -- scrollable region must be focusable for keyboard scrolling
    >
      <table aria-describedby={captionId} className="w-full text-left text-12">
        <thead>
          <tr className="ads:border-subtle border-gray-3 border-b">
            {pill.columns.map((column, index) => (
              <th
                className={classNames(
                  'w-px whitespace-nowrap py-2 align-bottom font-medium ads:text-metadata text-gray-9 leading-4',
                  index === 0 && 'pl-2.5',
                  index === pill.columns.length - 1 ? 'pr-2.5' : 'pr-3',
                )}
                key={column.key}
                scope="col"
              >
                <span
                  className="inline-block w-40 max-w-max whitespace-normal break-words align-top"
                  data-column-key={column.key}
                  data-testid="WORKSPACE_AGENT_TABLE_COLUMN_HEADER"
                >
                  {column.label}
                </span>
              </th>
            ))}
            {/* biome-ignore lint/a11y/noAriaHiddenOnFocusable: filler is not a column; hiding it keeps the header count equal to the data columns */}
            <th // NOSONAR -- empty layout cell, not a column, and not in the tab order
              aria-hidden="true"
              className="w-full p-0"
            />
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row, rowIndex) => (
            <tr
              className="ads:border-subtle border-gray-3 border-b last:border-b-0 ads:even:bg-base even:bg-gray-1"
              // Rows have no id of their own; position within the page is stable for read-only content.
              key={`${currentPage}-${rowIndex}`}
            >
              {pill.columns.map((column, index) => (
                <td
                  className={classNames(
                    'w-px whitespace-nowrap py-1.5 align-top leading-4',
                    index === 0 && 'pl-2.5',
                    index === pill.columns.length - 1 ? 'pr-2.5' : 'pr-3',
                  )}
                  key={column.key}
                >
                  <div
                    className="inline-block w-40 max-w-max whitespace-normal break-words align-top"
                    data-column-key={column.key}
                    data-testid="WORKSPACE_AGENT_TABLE_CELL"
                  >
                    <WorkspaceAgentTableCellContent
                      cell={row[column.key]}
                      column={column}
                      renderMoniker={renderMoniker}
                    />
                  </div>
                </td>
              ))}
              <td // NOSONAR -- empty layout cell, not a column, and not in the tab order
                aria-hidden="true"
                className="w-full p-0"
              />
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  ),
);

WorkspaceAgentTablePillScrollport.displayName = 'WorkspaceAgentTablePillScrollport';
